import { Divider, List, Text } from '@dixa/lib-shared-habla';
import { Message } from 'lib-shared-legacy-types';
import { ReadCustomFormResponse } from 'lib-shared-types-api';
import { memo, useEffect, useState } from 'react';

import { customFormsApi } from 'apis/customForms';
import { FormSubmissionData } from 'app/views/layout/views/settings/views/customForms';
import { formSubmissionConverter } from 'app/views/layout/views/settings/views/customForms/hooks/useCustomForms/formSubmissionConverter';
import { HtmlRenderer } from 'components/htmlRenderer';
import { sanitizationProfile } from 'utils/useEditorContext';

import { Message as MessageComponent } from '../message/message';

import { CustomFormSubmissionRow } from './CustomFormSubmissionRow';
import styles from './MessageCustomForm.module.scss';
import TextMessage, { TextMessageProps } from './TextMessage';
import { useTranslatePlainTextConversationMessage } from './utils/useTranslatePlainTextConversationMessage';

export type MessageCustomFormProps = TextMessageProps & {
  message: Message & { form_id: string; form_version: string };
};

export const MessageCustomForm = memo(function MessageWidgetChatForm(
  props: MessageCustomFormProps,
) {
  const { text, form_id, form_version, form_submission_id } = props.message;

  const [formSubmissionData, setFormSubmissionData] =
    useState<FormSubmissionData | null>(null);

  const { button, translation } = useTranslatePlainTextConversationMessage({
    text: text ?? undefined,
    originalLanguageCode: props.message.language ?? null,
  });
  const isModernForm = !!text;

  const { isError, isLoading, data } = customFormsApi.useReadFormQuery({
    form_id,
    version: form_version,
  });

  const form = data?.data;

  const [readFormSubmission] =
    customFormsApi.useLazyReadFormSubmissionDataQuery();

  useEffect(() => {
    async function fetchFormSubmissionData() {
      if (!form_submission_id || !form) {
        return;
      }

      try {
        const response = await readFormSubmission({
          form_id,
          form_version,
          submission_id: form_submission_id,
        }).unwrap();

        const data = formSubmissionConverter(
          response.data,
          form.attributes ?? [],
          // For Legacy forms we need to show all values
          // For newer forms that include formdata in the text we need to show only the file_list
          isModernForm
            ? (item) => item.value.data_type === 'file_list'
            : () => true,
        );
        setFormSubmissionData(data);
      } catch {
        setFormSubmissionData(null);
      }
    }

    fetchFormSubmissionData();
  }, [
    form_submission_id,
    readFormSubmission,
    form_id,
    form_version,
    form,
    isModernForm,
  ]);

  if (isLoading) {
    return (
      <MessageComponent
        author={props.message.author}
        content={<div>Loading the form...</div>}
      />
    );
  }

  if (isError) {
    return <TextMessage author={props.author} message={props.message} />;
  }

  return (
    <TextMessage
      {...props}
      content={
        isModernForm ? (
          <ModernFormContent
            text={text}
            translation={translation}
            form={form}
            formSubmissionData={formSubmissionData}
          />
        ) : (
          <LegacyFormContent
            form={form}
            formSubmissionData={formSubmissionData}
          />
        )
      }
      gutterButtons={button}
      translation={translation}
    />
  );
});

export function isMessengerCustomFormMessage(
  message: Message,
): message is MessageCustomFormProps['message'] {
  return Boolean(message.form_id) && Boolean(message.form_version);
}

/*
  The main difference between the modern and legacy content is that the conversation
  includes the form data in the text, while the legacy form does not.
  This means we have less control over the layout of the modern form,
  and we need to use the HtmlRenderer to render the text.
  Though it makes the form data able to appear in search results and previews.
*/
function ModernFormContent({
  text,
  translation,
  form,
  formSubmissionData,
}: {
  text: string | null;
  translation: ReturnType<
    typeof useTranslatePlainTextConversationMessage
  >['translation'];
  form: ReadCustomFormResponse['data'] | undefined;
  formSubmissionData: FormSubmissionData | null;
}) {
  const hasFiles = !!(form && form?.attributes?.length);
  return (
    <>
      <HtmlRenderer
        sanitizationProfileType={sanitizationProfile}
        html={
          translation?.translatedText ? translation.translatedText : text ?? ''
        }
      />
      {hasFiles && (
        <>
          <br />
          <Divider />
          <List className={styles.list}>
            {form.attributes?.map(({ id }) => (
              <CustomFormSubmissionRow
                key={id}
                label=""
                fieldSubmission={formSubmissionData?.submissionData?.[id]}
              />
            ))}
          </List>
        </>
      )}
    </>
  );
}

function LegacyFormContent({
  form,
  formSubmissionData,
}: {
  form: ReadCustomFormResponse['data'] | undefined;
  formSubmissionData: FormSubmissionData | null;
}) {
  if (!form) {
    return (
      <Text.Paragraph as="span" variant="2">
        Form data not available
      </Text.Paragraph>
    );
  }

  return (
    <List className={styles.list}>
      {form.attributes?.map(({ id, label }) => (
        <CustomFormSubmissionRow
          key={id}
          label={label}
          fieldSubmission={formSubmissionData?.submissionData?.[id]}
        />
      ))}
    </List>
  );
}
